import {useRef, useState} from 'react';
import {useRecoilValue} from 'recoil';
import {checkForRestrictedContent} from '@bidmii/common/lib/util/ContentCensorship';
import AlertDialog from '../common/dialogs/alertDialog';
import {userState} from '../recoil/user/userAtom';

// Approved wording (Jon, 2026-07-16, firm variant). Homeowners only - contractors
// stay hard-blocked by the server-side content screening instead.
const WARNING_TITLE = 'A quick warning before you send';
const WARNING_BODY =
  'Sharing your contact info is allowed — but know the risk.\n\n' +
  'Bidmii was built to protect homeowners: your money is held safely and only ' +
  'released when you approve the finished work. If a contractor moves the job off ' +
  'Bidmii, every one of those protections disappears. If things go wrong ' +
  'off-platform — bad work, a vanished deposit — we have no way to get your money back.';

/** Strips markup so the phone/email scan runs on the text a reader actually sees. */
export const htmlToPlainText = (html: string): string => {
  if (!html) return '';
  if (typeof DOMParser === 'undefined') return html;
  return (
    new DOMParser().parseFromString(html, 'text/html').body.textContent || ''
  );
};

interface WarnOptions {
  /** Extra paragraph appended to the warning body, e.g. the Q&A visibility line. */
  extraLine?: string;
  /** Primary button label, defaults to "I understand — send". */
  confirmLabel?: string;
  /** Called when the user picks "Edit message" instead of proceeding. */
  onEdit?: () => void;
  /** When true the check is skipped entirely (e.g. proposal already funded). */
  skip?: boolean;
}

/**
 * Warn-don't-block gate for homeowner-authored text that contains contact info
 * (Jon, 2026-07-16). Call confirmContactShare(text, proceed, opts): when the current
 * user is a homeowner and the text contains a phone number or email address, the
 * warning dialog opens and proceed() only runs on "I understand — send". Otherwise
 * proceed() runs immediately. Render contactShareWarningDialog once in the tree.
 */
export default function useOffPlatformWarning() {
  const user = useRecoilValue(userState);
  const [open, setOpen] = useState(false);
  const [confirmLabel, setConfirmLabel] = useState('I understand — send');
  const [body, setBody] = useState(WARNING_BODY);
  const pendingProceed = useRef<(() => void) | null>(null);
  const pendingEdit = useRef<(() => void) | null>(null);

  const confirmContactShare = (
    text: string,
    proceed: () => void,
    opts: WarnOptions = {},
  ) => {
    if (
      opts.skip ||
      user?.userType === 'BIDDER' ||
      !checkForRestrictedContent(htmlToPlainText(text))
    ) {
      proceed();
      return;
    }
    setBody(
      opts.extraLine ? `${WARNING_BODY}\n\n${opts.extraLine}` : WARNING_BODY,
    );
    setConfirmLabel(opts.confirmLabel || 'I understand — send');
    pendingProceed.current = proceed;
    pendingEdit.current = opts.onEdit || null;
    setOpen(true);
  };

  const handleConfirm = () => {
    setOpen(false);
    const proceed = pendingProceed.current;
    pendingProceed.current = null;
    pendingEdit.current = null;
    if (proceed) proceed();
  };

  const handleEdit = () => {
    setOpen(false);
    const onEdit = pendingEdit.current;
    pendingProceed.current = null;
    pendingEdit.current = null;
    if (onEdit) onEdit();
  };

  const contactShareWarningDialog = (
    <AlertDialog
      open={open}
      title={WARNING_TITLE}
      body={body}
      confirm={confirmLabel}
      close="Edit message"
      onConfirm={handleConfirm}
      onClose={handleEdit}
    />
  );

  return {confirmContactShare, contactShareWarningDialog};
}
